import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight, Heart, LayoutDashboard } from "lucide-react";
import { NAV, SERVER, ASSETS } from "@/data/content";
import { EASE } from "./Reveal";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
      className="fixed inset-x-0 top-0 z-50"
      data-testid="navbar"
    >
      <div
        className={`mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8 transition-[padding,background-color,border-color] duration-500 ${
          scrolled ? "py-3" : "py-5"
        }`}
      >
        <div
          className={`pointer-events-none absolute inset-0 border-b transition-all duration-500 ${
            scrolled ? "bg-[#0a0a0b]/70 backdrop-blur-xl border-white/[0.06]" : "bg-transparent border-transparent"
          }`}
        />

        <a href="#top" className="relative z-10 flex items-center gap-3" data-testid="nav-logo-link">
          <img src={ASSETS.logo} alt="Bone County Roleplay logó" className="h-9 w-9 object-contain" />
          <span className="font-display text-sm font-bold tracking-[0.18em] uppercase text-white">{SERVER.short}</span>
        </a>

        <nav className="relative z-10 hidden items-center gap-3 md:flex lg:gap-6 xl:gap-10" data-testid="nav-desktop">
          {NAV.map((n) => {
            const Tag = n.route ? Link : "a";
            const props = n.route ? { to: n.href } : { href: n.href, target: n.external ? "_blank" : undefined, rel: n.external ? "noreferrer" : undefined };
            return (
            <Tag
              key={n.label}
              {...props}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-").normalize("NFD").replace(/[\u0300-\u036f]/g, "")}`}
              className="link-underline inline-flex items-center gap-1 text-[13px] font-medium tracking-wide text-white/70 transition-colors duration-300 hover:text-white"
            >
              {n.label}
              {n.external && <ArrowUpRight size={13} className="opacity-60" />}
            </Tag>
          );})}
          <Link
            to="/tamogatas"
            data-testid="nav-donate-button"
            className="btn-donate inline-flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold"
          >
            <Heart size={14} className="donate-heart" fill="currentColor" />
            Támogatás
          </Link>
          <div className="flex items-center gap-3">
            <a
            href={SERVER.mtaLink}
            data-testid="nav-join-button"
            className="btn-glow inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[13px] font-semibold text-black"
          >
            Csatlakozás
            </a>
            <Link
              to="/ucp"
              data-testid="nav-ucp-button"
              className="btn-ucp inline-flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold"
            >
              <LayoutDashboard size={15} />
              UCP
            </Link>
          </div>
        </nav>

        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="nav-menu-open"
          aria-label="Menü megnyitása"
          className="relative z-10 grid h-10 w-10 place-items-center rounded-full border border-white/10 text-white md:hidden"
        >
          <Menu size={18} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] flex flex-col bg-[#0a0a0b]/95 backdrop-blur-2xl md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-5">
              <span className="font-display text-sm font-bold tracking-[0.18em] uppercase">{SERVER.short}</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="nav-menu-close"
                aria-label="Menü bezárása"
                className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex flex-1 flex-col justify-center gap-2 px-6">
              {NAV.map((n, i) => (
                <motion.a
                  key={n.label}
                  href={n.href}
                  target={n.external ? "_blank" : undefined}
                  rel={n.external ? "noreferrer" : undefined}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.6, ease: EASE }}
                  data-testid={`nav-mobile-link-${i}`}
                  className="font-display border-b border-white/[0.06] py-5 text-3xl font-bold text-white"
                >
                  {n.label}
                </motion.a>
              ))}
              <div className="flex flex-wrap items-center gap-3 pt-6">
                <a href={SERVER.mtaLink} onClick={() => setOpen(false)} data-testid="nav-mobile-join" className="btn-glow inline-flex items-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-black">
                  Csatlakozás
                </a>
                <Link to="/ucp" onClick={() => setOpen(false)} data-testid="nav-mobile-ucp" className="btn-ucp inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold">
                  <LayoutDashboard size={16} />
                  UCP
                </Link>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + (NAV.length + 1) * 0.07, duration: 0.6, ease: EASE }}
                className="pt-6"
              >
                <Link
                  to="/tamogatas"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-donate"
                  className="btn-donate inline-flex items-center gap-3 px-7 py-4 text-xl font-semibold"
                >
                  <Heart size={20} className="donate-heart" fill="currentColor" />
                  Támogatás
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
